/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import ExpulsionStage from 'linced-app/enums/expulsion-stage';
import { hasRole } from 'linced-app/utils/roles';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import MedicationType from 'linced-app/enums/medical/medication-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import Staff from '@linc-technologies/linced-common/enums/roles/staff';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { IncidentOutcome } from 'linced-app/enums/behaviours';
import { filterBy } from '@linc-technologies/linced-common/utils/arrays';

const GROUP_PH_ICONS = {
  basic: 'gear',
  app: 'device-mobile-camera',
  finance: 'currency-dollar-simple',
  nzMOE: 'building-apartment',
  medications: 'first-aid-kit',
  notifications: 'bell',
  progress: 'trend-up',
  parents: 'eye',
};

export default class Menu extends Component {
  @service
  features;

  @service
  layoutControls;

  @service
  options;

  @service
  permissions;

  @service
  reportingGroupBrowser;

  @service
  roles;

  @service
  router;

  @service
  session;

  @service
  students;

  @service
  strings;

  Scopes = Scopes;
  Jurisdiction = Jurisdiction;
  MedicationType = MedicationType;
  @tracked isWideList = false;
  @tracked
  menuLinksHidden = false;

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    if (this.isWideList && !this._lastIsWideList) {
      this.menuLinksHidden = true;
      this.layoutControls.notifyLayoutChange();
    } else if (!this.isWideList) {
      this.menuLinksHidden = false;
      this.layoutControls.notifyLayoutChange();
    }
    this._lastIsWideList = this.isWideList;
  }

  @tracked _selectedHub = null;

  @computed('_selectedHub', 'router.currentRouteName')
  get selectedHub() {
    let routeName = this.router.currentRouteName || '';
    // Auto-activate reporting only if the user hasn't explicitly switched to another hub
    if (
      routeName.startsWith('reporting') &&
      (this._selectedHub === null || this._selectedHub === 'reporting')
    ) {
      return 'reporting';
    }
    // Clear reporting selection when navigating away without explicitly choosing another hub
    if (
      this._selectedHub === 'reporting' &&
      !routeName.startsWith('reporting')
    ) {
      return null;
    }
    return this._selectedHub;
  }

  init() {
    super.init(...arguments);
    if (this.permissions.meet(Scopes.TENANT_ADMIN)) {
      const saved = sessionStorage.getItem('hub:selectedHub');
      this._selectedHub = saved || 'admin';
    }
  }

  groupPhIcons = GROUP_PH_ICONS;
  applicationFormUnsaved = false;

  @computed('isWideList', 'session.[isAuthenticated,scopes]')
  get sidenavClasses() {
    let classes = ['site-nav-container'];
    if (
      !this.get('session.isAuthenticated') ||
      !this.permissions.meet(Scopes.TENANT_ADMIN)
    ) {
      classes.push('no-left-nav');
    } else if (this.isWideList) {
      classes.push('wide-list');
    }

    return classes.join(' ');
  }

  get leftSideBarLockedOpen() {
    if (this.isWideList) {
      return 'gt-lg';
    }
    return 'gt-md';
  }

  @computed(
    'model.optionGroups.@each.{heroOnly,key}',
    'session.scopes.[]',
    'features.{pupilBillingAvailable,heroSuiteEnabled}',
  )
  get filteredOptionGroups() {
    if (isEmpty(this.model) || isEmpty(this.model.optionGroups)) {
      return [];
    }

    return this.model.optionGroups.filter((group) => {
      if (group.key === 'pd') {
        return false;
      }
      if (group.heroOnly && !this.permissions.meet(Scopes.LINCED_MANAGER)) {
        return false;
      }
      if (group.key === 'finance' && !this.features.pupilBillingEnabled) {
        return false;
      }
      if (group.key === 'medications' && !this.features.heroSuiteEnabled) {
        return false;
      }
      return true;
    });
  }

  @computed('features.heroSuiteAvailable', 'session.scopes.[]')
  get studentsSectionVisible() {
    return (
      this.features.heroSuiteAvailable ||
      this.permissions.meet(Scopes.LINCED_MANAGER)
    );
  }

  @computed('model.incidents.@each.outcome')
  get incidentsRequiringActions() {
    if (isEmpty(this.model) || isEmpty(this.model.incidents)) {
      return [];
    }
    return this.model.incidents.filter((incident) => {
      return incident.outcome === IncidentOutcome.UNRESOLVED;
    });
  }

  @computed('model.suspensions.@each.actionRequired')
  get suspensionsRequiringActions() {
    if (isEmpty(this.model) || isEmpty(this.model.suspensions)) {
      return [];
    }
    return this.model.suspensions.filter(
      (suspension) => suspension.actionRequired,
    );
  }

  @optionAlias('hero:features:expulsions')
  expulsionsEnabled;

  @computed('model.expulsions.@each.stage')
  get expulsionsRequiringActions() {
    if (isEmpty(this.model) || isEmpty(this.model.expulsions)) {
      return [];
    }
    return this.model.expulsions.filter((expulsion) => {
      return expulsion.stage === ExpulsionStage.PENDING_EXPULSION;
    });
  }

  @computed('session.{roles.[],scopes.[]}')
  get canManageExpulsions() {
    return (
      this.roles.hasAny(Staff.PRINCIPAL, Staff.PRINCIPAL_S_DELEGATE) ||
      this.permissions.meet(Scopes.LINCED_ADMIN)
    );
  }

  @hasRole(Staff.PROCESS_ENROLMENT_APPLICATIONS)
  isEnrolmentApplicationProcessor;

  @computed('isEnrolmentApplicationProcessor', 'session.scopes.[]')
  get canViewEnrolmentApplications() {
    return (
      this.isEnrolmentApplicationProcessor ||
      this.permissions.meet(Scopes.LINCED_MANAGER)
    );
  }

  @optionAlias('hero:nzMOE:enrolArrivalsLeaversEnabled')
  nzMoeEnrolArrivalsLeaversEnabled;

  @optionAlias('hero:features:bookings')
  bookingsEnabled;

  @optionAlias('hero:behaviours:wellbeingEnabled')
  wellbeingEnabled;

  @computed('model.medications.@each.{deleted,person_id}')
  get nonDeletedSchoolMedications() {
    if (!isEmpty(this.model) && !isEmpty(this.model.medications)) {
      return this.model.medications.filter(
        (medication) => !medication.person_id && !medication.deleted,
      );
    }
    return [];
  }

  @optionAlias('medications:levels:standardAutoinjectors')
  minStandardAutoinjectorCount;

  @computed('nonDeletedSchoolMedications.@each.quantity')
  get standardAutoinjectorCount() {
    return filterBy(
      this.nonDeletedSchoolMedications,
      'type',
      MedicationType.AUTOINJECTOR___STANDARD,
    ).reduce((count, medication) => count + medication.get('quantity'), 0);
  }

  @computed('minStandardAutoinjectorCount', 'standardAutoinjectorCount')
  get standardAutoinjectorCountAlert() {
    return this.standardAutoinjectorCount < this.minStandardAutoinjectorCount;
  }

  @optionAlias('medications:levels:juniorAutoinjectors')
  minJuniorAutoinjectorCount;

  @computed('nonDeletedSchoolMedications.@each.quantity')
  get juniorAutoinjectorCount() {
    return filterBy(
      this.nonDeletedSchoolMedications,
      'type',
      MedicationType.AUTOINJECTOR___JUNIOR,
    ).reduce((count, medication) => count + medication.get('quantity'), 0);
  }

  @computed('minJuniorAutoinjectorCount', 'juniorAutoinjectorCount')
  get juniorAutoinjectorCountAlert() {
    return this.juniorAutoinjectorCount < this.minJuniorAutoinjectorCount;
  }

  @optionAlias('medications:levels:asthmaInhalers')
  minAsthmaInhalerCount;

  @computed('nonDeletedSchoolMedications.@each.quantity')
  get asthmaInhalerCount() {
    return filterBy(
      this.nonDeletedSchoolMedications,
      'type',
      MedicationType.ASTHMA_INHALER___GENERAL_USE,
    ).reduce((count, medication) => count + medication.get('quantity'), 0);
  }

  @computed('minAsthmaInhalerCount', 'asthmaInhalerCount')
  get asthmaInhalerCountAlert() {
    return this.asthmaInhalerCount < this.minAsthmaInhalerCount;
  }

  @computed(
    'standardAutoinjectorCountAlert',
    'juniorAutoinjectorCountAlert',
    'asthmaInhalerCountAlert',
  )
  get medicationAlert() {
    return (
      this.standardAutoinjectorCountAlert ||
      this.juniorAutoinjectorCountAlert ||
      this.asthmaInhalerCountAlert
    );
  }

  @action
  async switchSchool() {
    await this.session.switchSchool();
  }

  @action
  changeHub(newHub) {
    if (this.selectedHub === newHub) {
      sessionStorage.removeItem('hub:selectedHub');
      this._selectedHub = null;
    } else {
      sessionStorage.setItem('hub:selectedHub', newHub);
      this._selectedHub = newHub;
      this.menuLinksHidden = false;
    }
    this.layoutControls.notifyLayoutChange();
  }

  @action
  closeSelectors(...args) {
    let fn = this.onCloseSelectors;
    if (fn) {
      fn(...args);
    } else {
      let body = document.querySelector('body');
      if (body) {
        body.className = body.className.replace(/ no-scroll/g, '');
      }
    }
  }

  @action
  toggleExpandedMenuItem(value, ev) {
    let fn = this.onToggleExpandedMenuItem;
    if (fn) {
      fn(value, ev);
    }
  }

  @action
  menuItemAvailable(group) {
    switch (group.key) {
      case 'parents':
        return this.features.reportingPowerUpAvailable;
      case 'progress':
        return this.features.milestonesPowerUpAvailable;
    }
    return true;
  }

  @action
  featurePackRoute(group) {
    switch (group.key) {
      case 'parents':
        if (!this.features.reportingPowerUpEnabled) {
          return 'feature-packs.parent-view';
        }
        break;
      case 'progress':
        if (!this.features.milestonesPowerUpEnabled) {
          return 'feature-packs.milestones';
        }
    }
  }

  @action
  goToReporting() {
    sessionStorage.setItem('hub:selectedHub', 'reporting');
    this._selectedHub = 'reporting';
    this.closeSelectors();
    this.router.transitionTo('reporting');
  }

  @action
  selectReportingGroup(group) {
    this.router.transitionTo('reporting.group', group.get('id'));
    this.closeSelectors();
  }

  @action
  selectReportingStudent(group, student) {
    this.router.transitionTo(
      'reporting.group.student',
      group.get('id'),
      student.get('id'),
    );
    this.closeSelectors();
  }

  @action
  toggleReportingGroupStudents(groupId) {
    let expandedGroups = this.reportingGroupBrowser.expandedGroups;
    let pos = expandedGroups.indexOf(groupId);
    if (pos > -1) {
      expandedGroups.splice(pos, 1);
    } else {
      expandedGroups.push(groupId);
    }
  }
}
